/**
 * `expo-background-task` no browser.
 *
 * WEB: não existe execução com o app fechado garantida. A tarefa registrada roda:
 *  - em intervalo (`minimumInterval`, em minutos) enquanto a página está aberta;
 *  - quando a conexão volta (`online`);
 *  - quando o service worker avisa (`{type:"tirvu-sync"}`), disparado por Background Sync
 *    ou Periodic Background Sync nos browsers que suportam (Chromium).
 */
import { __executar, __marcarRegistrada } from "@/platform/task-manager";

export enum BackgroundTaskStatus {
  Restricted = 1,
  Available = 2,
}

export enum BackgroundTaskResult {
  Success = 1,
  Failed = 2,
}

const timers = new Map<string, ReturnType<typeof setInterval>>();
const nomes = new Set<string>();
let ouvindo = false;

function ouvirGatilhos() {
  if (ouvindo || typeof window === "undefined") return;
  ouvindo = true;
  window.addEventListener("online", () => nomes.forEach((n) => void __executar(n)));
  navigator.serviceWorker?.addEventListener("message", (ev) => {
    if (ev.data?.type === "tirvu-sync") nomes.forEach((n) => void __executar(n));
  });
}

export async function getStatusAsync(): Promise<BackgroundTaskStatus> {
  return BackgroundTaskStatus.Available;
}

export async function registerTaskAsync(nome: string, opts: { minimumInterval?: number } = {}): Promise<void> {
  ouvirGatilhos();
  nomes.add(nome);
  __marcarRegistrada(nome, true);
  const minutos = Math.max(1, opts.minimumInterval ?? 15);
  if (timers.has(nome)) clearInterval(timers.get(nome)!);
  timers.set(nome, setInterval(() => void __executar(nome), minutos * 60_000));
  // Pede ao SW sincronização periódica quando o browser permitir (Chromium instalado).
  try {
    const reg: any = await navigator.serviceWorker?.ready;
    if (reg?.periodicSync) {
      const perm = await navigator.permissions?.query({ name: "periodic-background-sync" as PermissionName }).catch(() => null);
      if (!perm || perm.state === "granted") await reg.periodicSync.register("tirvu-envio-periodico", { minInterval: minutos * 60_000 });
    }
  } catch {
    /* sem suporte */
  }
}

export async function unregisterTaskAsync(nome: string): Promise<void> {
  nomes.delete(nome);
  __marcarRegistrada(nome, false);
  const t = timers.get(nome);
  if (t) clearInterval(t);
  timers.delete(nome);
}

export async function triggerTaskWorkerForTestingAsync(): Promise<boolean> {
  await Promise.all(Array.from(nomes).map((n) => __executar(n)));
  return true;
}
